import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Info,
  Check,
  Upload,
  AlertCircle,
  Eye,
  Trash2,
  Plus,
  ArrowLeft,
  Calendar,
  Package,
  FileText,
  User,
  MapPin,
  Truck,
  X,
  File,
  Loader2,
  CheckCircle2,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import styles from './AddMaterialReceipt.module.css';

const AddMaterialReceipt = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data Options
  const [poOptions, setPoOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // Form State
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [formData, setFormData] = useState({
    received_date: new Date().toISOString().split('T')[0],
    challan_number: '',
    transporter_name: '',
    vehicle_number: '',
    received_by: '',
    storage_location: '',
    status: 'partial',
    remarks: '',
    quality_remarks: '',
    mr_document: null
  });

  const [qualityCheck, setQualityCheck] = useState({
    is_outer_packing_intact: false,
    is_visual_damage_observed: false,
    is_matching_specifications: true,
    is_certification_enclosed: false
  });

  const [receivedItems, setReceivedItems] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    fetchInitialData();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchInitialData = async () => {
    try {
      const [posRes, empsRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/employees-dropdown')
      ]);

      if (posRes.data.success) {
        const poList = posRes.data.data?.data || [];
        setPoOptions(poList.map(po => ({
          value: po.id,
          label: po.po_number,
          ...po
        })));
      }

      if (empsRes.data.success) {
        setEmployeeOptions(empsRes.data.data.map(emp => ({
          value: emp.id,
          label: emp.employee_name
        })));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load required options');
    }
  };

  const handlePOChange = async (option) => {
    setSelectedPO(option);
    if (!option) {
      setPoDetails(null);
      setReceivedItems([]);
      return;
    }

    setLoading(true);
    try {
      // Fetch full PO details if items are not in the list
      const response = await api.get(`/purchase-orders/${option.value}`);
      if (response.data.success) {
        const fullPO = response.data.data;
        setPoDetails(fullPO);
        // Initialize received items
        setReceivedItems((fullPO.items || []).map(item => ({
          po_item_id: item.id,
          name: item.item_name || 'Standard Item',
          ordered: item.quantity,
          prev: item.received_qty || 0,
          balance: item.quantity - (item.received_qty || 0),
          now: '',
          unit: item.unit?.unit_name || 'Units',
          item_remarks: ''
        })));
      }
    } catch (error) {
      toast.error('Failed to load PO items');
      // Fallback to option data if ID-based fetch fails
      setPoDetails(option);
    } finally {
      setLoading(false);
    }
  };

  const handleQtyChange = (index, value) => {
    setReceivedItems(prev => prev.map((item, i) => {
      if (i === index) {
        const parsed = parseFloat(value) || 0;
        return {
          ...item,
          now: value,
          balance: item.ordered - item.prev - parsed
        };
      }
      return item;
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!selectedPO) {
      toast.error('Please select a PO (Purchase Order)');
      return;
    }

    const itemsToSubmit = receivedItems
      .filter(item => item.now > 0)
      .map(item => ({
        po_item_id: item.po_item_id,
        received_qty: item.now,
        item_remarks: item.item_remarks
      }));

    if (itemsToSubmit.length === 0) {
      toast.error('Please enter quantity for at least one item');
      return;
    }

    setSubmitLoading(true);
    const fd = new FormData();
    fd.append('purchase_order_id', selectedPO.value);
    fd.append('status', formData.status);
    fd.append('received_date', formData.received_date);
    fd.append('received_by', formData.received_by);
    fd.append('challan_number', formData.challan_number);
    fd.append('storage_location', formData.storage_location || '');
    fd.append('transporter_name', formData.transporter_name || '');
    fd.append('vehicle_number', formData.vehicle_number || '');
    fd.append('remarks', formData.remarks || '');
    fd.append('quality_remarks', formData.quality_remarks || '');

    // Append quality checks
    Object.keys(qualityCheck).forEach(key => {
      fd.append(key, qualityCheck[key] ? 1 : 0);
    });

    // Append items
    itemsToSubmit.forEach((item, index) => {
      fd.append(`items[${index}][po_item_id]`, item.po_item_id);
      fd.append(`items[${index}][received_qty]`, item.received_qty);
      if (item.item_remarks) {
        fd.append(`items[${index}][item_remarks]`, item.item_remarks);
      }
    });

    if (formData.mr_document) {
      fd.append('mr_document', formData.mr_document);
    }

    try {
      const response = await api.post('/material-receipts', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success('Material receipt recorded successfully!');
        navigate('/receipts');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit receipt');
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className={`${styles['add-receipt-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}  ${isMobile ? styles['mobile-view'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <header className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>Add Material Receipt</h1>
              <p>Record incoming materials for PO: <span className={styles['highlight-po']}>{selectedPO?.label || 'None Selected'}</span></p>
            </div>
          </header>

          <div className={styles['po-selection-bar']}>
            <div className={styles['po-selector-wrapper']}>
              <label>Selection PO (Purchase Order)</label>
              <CustomSelect
                options={poOptions}
                value={selectedPO}
                onChange={handlePOChange}
                placeholder="Search PO Number..."
                isSearchable={true}
              />
            </div>
          </div>

          {/* Section 1: PO Summary */}
          <section className={styles['section-title']}>
            <span>Section 1: PO Summary</span>
            <span className={styles['status-tag']}>{poDetails ? 'Loaded' : 'Pending Selection'}</span>
          </section>
          <div className={styles['form-section-card']}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px' }}>
                <Loader2 className={styles['spinner']} />
                <span>Fetching items and vendor schedules...</span>
              </div>
            ) : poDetails ? (
              <div className={styles['po-summary-grid']}>
                <div className={styles['summary-item-receipts']}>
                  <label>Vendor</label>
                  <span className={styles['value']}>{poDetails.vendor?.vendor_name || '---'}</span>
                </div>
                <div className={styles['summary-item-receipts']}>
                  <label>Project</label>
                  <span className={styles['value']}>{poDetails.project?.project_name || '---'}</span>
                </div>
                <div className={styles['summary-item-receipts']}>
                  <label>Total Value</label>
                  <span className={styles['value']}>₹ {parseFloat(poDetails.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className={[styles['summary-item-receipts'], styles['completion-status']].join(' ')}>
                  <label>Expected Delivery</label>
                  <span className={styles['value']}>{poDetails.expected_delivery_date ? new Date(poDetails.expected_delivery_date).toLocaleDateString('en-IN') : '---'}</span>
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', color: '#64748b', textAlign: 'center' }}>
                Please select a PO (Purchase Order) to load material details.
              </div>
            )}
          </div>

          {/* Section 2: Material Items Receipt Table */}
          <section className={styles['section-title']}>
            <span>Section 2: Material Items Receipt Table</span>
          </section>
          <div className={`${styles['form-section-card']} ${isMobile ? styles['no-bg-mobile'] : ''}`} style={{ padding: isMobile ? '0' : '0' }}>
            {!isMobile ? (
              <div className={styles['table-scroll']}>
                <table className={styles['items-table']}>
                  <thead>
                    <tr>
                      <th>Item Description</th>
                      <th style={{ width: '120px' }}>Ordered</th>
                      <th style={{ width: '120px' }}>Prev. Received</th>
                      <th style={{ width: '120px' }}>Balance</th>
                      <th style={{ width: '150px' }}>Receiving Now</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivedItems.length > 0 ? (
                      receivedItems.map((item, index) => (
                        <tr key={index}>
                          <td className={styles['item-name']}>{item.name}</td>
                          <td className={styles['qty-value']}>{item.ordered}</td>
                          <td className={styles['qty-value']}>{item.prev}</td>
                          <td className={`${styles['balance-highlight']} ${item.balance > 0 ? styles['pending'] : ''}`}>
                            {item.balance}
                          </td>
                          <td>
                            <input
                              type="number"
                              className={styles['receiving-input']}
                              value={item.now}
                              min="0"
                              max={item.ordered - item.prev}
                              onChange={(e) => handleQtyChange(index, e.target.value)}
                            />
                          </td>
                          <td className={styles['unit-label']}>{item.unit}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          Choose a PO to display items.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles['item-cards-mobile']}>
                {receivedItems.length > 0 ? (
                  receivedItems.map((item, index) => (
                    <div key={index} className={styles['item-card-mobile']}>
                      <div className={styles['item-card-header']}>
                        <span className={styles['item-name-mobile']}>{item.name}</span>
                        <span className={styles['unit-pill-mobile']}>{item.unit}</span>
                      </div>

                      <div className={styles['item-card-grid-mobile']}>
                        <div className={styles['card-item-stat']}>
                          <label>Ordered</label>
                          <span>{item.ordered}</span>
                        </div>
                        <div className={styles['card-item-stat']}>
                          <label>Prev. Rec.</label>
                          <span>{item.prev}</span>
                        </div>
                        <div className={`${styles['card-item-stat']} ${item.balance > 0 ? styles['has-balance'] : ''}`}>
                          <label>Balance</label>
                          <span>{item.balance}</span>
                        </div>
                      </div>

                      <div className={styles['card-receiving-action']}>
                        <label>Receiving Now</label>
                        <div className={styles['input-with-max']}>
                          <input
                            type="number"
                            className={styles['receiving-input-mobile']}
                            value={item.now}
                            min="0"
                            max={item.ordered - item.prev}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                          />
                          <span className={styles['max-hint']}>max {item.ordered - item.prev}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles['no-items-card']}>
                    <Package size={32} />
                    <p>Select a PO (Purchase Order) to view items</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 3 & 4 Grid */}
          <div className={styles['forms-grid']}>
            <div className={styles['left-form']}>
              <section className={styles['section-title']}>
                <span>Section 3: Receipt Details</span>
              </section>
              <div className={styles['form-section-card']}>
                <div className={styles['input-grid']}>
                  <div className={styles['form-group']}>
                    <label>Date Received</label>
                    <CustomDatePicker
                      selected={formData.received_date}
                      onChange={(date) => setFormData(prev => ({ ...prev, received_date: date.toISOString().split('T')[0] }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="partial">Partial Shipment</option>
                      <option value="completed">Fully Completed</option>
                    </select>
                  </div>
                  <div className={styles['form-group']}>
                    <label>Challan / Invoice #</label>
                    <input
                      type="text"
                      placeholder="e.g. INV/2026/001"
                      value={formData.challan_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, challan_number: e.target.value }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Received By</label>
                    <CustomSelect
                      options={employeeOptions}
                      value={employeeOptions.find(opt => opt.value === formData.received_by)}
                      onChange={(opt) => setFormData(prev => ({ ...prev, received_by: opt.value }))}
                      placeholder="Select Employee..."
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Transporter Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Reliable Logistics"
                      value={formData.transporter_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, transporter_name: e.target.value }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Vehicle Number</label>
                    <input
                      type="text"
                      placeholder="e.g. GJ-01-XX-0001"
                      value={formData.vehicle_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_number: e.target.value }))}
                    />
                  </div>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Storage / Warehouse Location</label>
                    <input
                      type="text"
                      placeholder="e.g. Main Warehouse - Bay 4"
                      value={formData.storage_location}
                      onChange={(e) => setFormData(prev => ({ ...prev, storage_location: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['right-form']}>
              <section className={styles['section-title']}>
                <span>Section 4: Quality Check</span>
              </section>
              <div className={styles['form-section-card']}>
                <div className={styles['checklist-grid']}>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_outer_packing_intact ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_outer_packing_intact: !qualityCheck.is_outer_packing_intact })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_outer_packing_intact && <Check size={14} />}</div>
                    <span>Outer Packing Intact</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_visual_damage_observed ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_visual_damage_observed: !qualityCheck.is_visual_damage_observed })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_visual_damage_observed && <Check size={14} />}</div>
                    <span>Visual Damage Observed</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_matching_specifications ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_matching_specifications: !qualityCheck.is_matching_specifications })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_matching_specifications && <Check size={14} />}</div>
                    <span>Matching Specifications</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_certification_enclosed ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_certification_enclosed: !qualityCheck.is_certification_enclosed })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_certification_enclosed && <Check size={14} />}</div>
                    <span>Certification Enclosed</span>
                  </div>
                </div>

                <div className={styles['form-group']}>
                  <label>Quality Remarks</label>
                  <textarea
                    placeholder="Describe any quality defects or certification missing details..."
                    value={formData.quality_remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, quality_remarks: e.target.value }))}
                  ></textarea>
                </div>

                <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                  <label>Internal Audit Remarks</label>
                  <textarea
                    placeholder="Internal warehouse or logistics notes..."
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    className={styles['audit-remarks']}
                  ></textarea>
                </div>

                <div className={styles['upload-section']}>
                  <label>Upload Document/Photo<span className={`${styles.required}`}>*</span></label>

                  <div className={styles['upload-area']} style={{ cursor: 'default' }}>
                    <Upload size={24} className={styles['upload-icon']} />
                    <span className={styles['upload-text']}>Select source to upload</span>
                    <span className={styles['upload-hint']}>PNG, JPG up to 10MB</span>

                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', width: '100%', justifyContent: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        id="camera-upload"
                        onChange={(e) => setFormData(prev => ({ ...prev, mr_document: e.target.files[0] }))}
                      />
                      <label htmlFor="camera-upload" className={styles['btn-cancel']} style={{ flex: 1, maxWidth: '160px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', background: 'white', margin: 0 }}>
                        <Camera size={18} /> Camera
                      </label>

                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id="gallery-upload"
                        onChange={(e) => setFormData(prev => ({ ...prev, mr_document: e.target.files[0] }))}
                      />
                      <label htmlFor="gallery-upload" className={styles['btn-cancel']} style={{ flex: 1, maxWidth: '160px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', background: 'white', margin: 0 }}>
                        <ImageIcon size={18} /> Gallery
                      </label>
                    </div>
                  </div>

                  {formData.mr_document && (
                    <div className={styles['file-list']}>
                      <div className={styles['file-item']}>
                        <div className={styles['file-info']}>
                          <File size={16} className={styles['file-icon']} />
                          <div className={styles['file-details']}>
                            <span className={styles['file-name']}>{formData.mr_document.name}</span>
                            <span className={styles['file-size']}>{(formData.mr_document.size / (1024 * 1024)).toFixed(2)} MB</span>
                          </div>
                        </div>
                        <button type="button" className={styles['remove-file-btn']} onClick={() => setFormData(prev => ({ ...prev, mr_document: null }))}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <footer className={styles['form-footer']}>
            <div className={styles['footer-hint']}>
              <AlertCircle size={18} color="#f59e0b" />
              <span>Submission will automatically update the PO fulfillment status.</span>
            </div>
            <div className={styles['footer-actions']}>
              <button className={styles['btn-cancel']} onClick={() => navigate(-1)}>Cancel</button>
              <button
                className={styles['btn-submit']}
                onClick={handleSubmit}
                disabled={submitLoading}
              >
                {submitLoading ? <Loader2 size={18} className={styles['spinner']} /> : <CheckCircle2 size={18} />}
                <span>{submitLoading ? 'Submitting...' : 'Save Material Receipt'}</span>
              </button>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default AddMaterialReceipt;

